import { Classes, Dialog } from "@blueprintjs/core"
import { observer } from "mobx-react"
import { DatePicker } from "modules/common/components/form/DatePicker"
import { LocalLoader } from "modules/common/components/loader/Loader"
import { MoneyInput } from "modules/common/components/money/MoneyInput"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import {
  DefaultSelectedOption,
  filterItemPredicate,
  renderSingleOption,
  SimpleSelect,
} from "modules/common/services/form/select"
import { formatDate } from "modules/common/services/formatting/date"
import moment from "moment"
import React, { useEffect, useMemo, useState } from "react"
import { EmployerType } from "../../models/employer"
import styles from "./TransferDialog.module.scss"

interface TransferDialogProps {
  isOpen: boolean
  onClose: () => void
  employer: EmployerType
  positions: SelectItem[]
  departments: SelectItem[]
  /** Организации (портфели проектов) */
  portfolios: SelectItem[]
  /** Право на штатку: оклад и премию видно и можно менять */
  canSalary: boolean
}

const NO_GRADE = "__none__"

const nextMonthStart = () => moment().add(1, "month").startOf("month").toDate()

const dateText = (value: string) => moment(value, ["DD.MM.YYYY", "DD.MM.YYYY HH:mm:ss"]).format("DD.MM.YYYY")

/**
 * Перевод на должность: сохраняется сразу, отдельно от карточки. Должность, дата (не задним числом),
 * подразделение, организация, ранг новой должности; оклад и премия — только с правом на штатку, подставляются
 * из условий, действующих на дату перевода. Перевод в другую организацию — с датой приёма в неё (дата перевода),
 * стаж сохраняется, если не отметить «Сбросить стаж»
 */
export const TransferDialog = observer(
  ({ isOpen, onClose, employer, positions, departments, portfolios, canSalary }: TransferDialogProps) => {
    const currentPositionId = employer.employePositionId
    const currentGradeGuid = employer.employerPositions?.[0]?.gradeGuid || null
    const currentDepartmentId = employer.department?.id || ""
    const currentPortfolioId = employer.portfolioId || ""
    const [positionId, setPositionId] = useState("")
    const [date, setDate] = useState<Date | null>(nextMonthStart())
    const [departmentId, setDepartmentId] = useState("")
    const [portfolioId, setPortfolioId] = useState("")
    const [resetSeniority, setResetSeniority] = useState(false)
    const [grades, setGrades] = useState<{ guid: string; gradeName: string; percent: number }[]>([])
    const [gradeGuid, setGradeGuid] = useState(NO_GRADE)
    const [conditions, setConditions] = useState<any[]>([])
    const [salary, setSalary] = useState<number | null>(null)
    const [bonus, setBonus] = useState<number | null>(null)
    const [loading, setLoading] = useState(false)

    // при открытии - чистая форма; условия оплаты подгружаем, только если их можно видеть
    useEffect(() => {
      if (!isOpen) {
        return
      }

      // по умолчанию - текущие должность и ранг: перевод в другое подразделение - сменить только его
      setPositionId(currentPositionId || "")
      setDate(nextMonthStart())
      setDepartmentId(employer.department?.id || "")
      setPortfolioId(employer.portfolioId || "")
      setResetSeniority(false)
      setGrades([])
      setGradeGuid(currentGradeGuid || NO_GRADE)
      if (currentPositionId) {
        employer.loadPositionGrades(currentPositionId).then((items: any[]) => setGrades(items))
      }
      setSalary(null)
      setBonus(null)
      setConditions([])

      if (canSalary) {
        employer.loadConditions().then((rows: any[]) => setConditions(rows))
      }
    }, [isOpen, employer, canSalary, currentPositionId, currentGradeGuid])

    // оклад и премия - действующие на месяц перевода
    useEffect(() => {
      if (!canSalary || !date) {
        return
      }

      const month = moment(date).startOf("month")
      const parse = (since: string) => moment(since, ["DD.MM.YYYY", moment.ISO_8601], true)
      const current = [...conditions]
        .filter((r) => !parse(r.since).isAfter(month, "month"))
        .sort((a, b) => parse(b.since).valueOf() - parse(a.since).valueOf())[0]

      setSalary(current ? current.salary : 0)
      setBonus(current ? current.bonus : 0)
    }, [conditions, date, canSalary])

    const selectPosition = async (id: string) => {
      setPositionId(id)
      setGradeGuid(id === currentPositionId ? currentGradeGuid || NO_GRADE : NO_GRADE)
      setGrades(await employer.loadPositionGrades(id))
    }

    const positionItems = positions
    const gradeItems: SelectItem[] = useMemo(
      () => [
        { id: NO_GRADE, label: "Без ранга" },
        ...grades.map((g) => ({ id: g.guid, label: `${g.gradeName} — ${g.percent}%` })),
      ],
      [grades]
    )

    const today = moment().startOf("day")
    const dateError = !date
      ? "Укажите дату перевода"
      : moment(date).isBefore(today)
        ? "Задним числом перевести нельзя"
        : ""
    // другая организация - перевод с новой датой приёма; первое заполнение организации - не перевод
    const organizationChanged = !!currentPortfolioId && portfolioId !== currentPortfolioId
    // перевод - смена должности, подразделения или организации (ранг текущей должности меняется в карточке)
    const changed =
      positionId !== currentPositionId || departmentId !== currentDepartmentId || portfolioId !== currentPortfolioId
    const canSave = !!positionId && changed && !dateError && !loading

    const save = async () => {
      if (!canSave || !date) {
        return
      }

      setLoading(true)
      const ok = await employer.transfer({
        positionId,
        date: formatDate(date),
        departmentId: departmentId || null,
        portfolioId: portfolioId || null,
        resetSeniority: organizationChanged && resetSeniority,
        gradeGuid: gradeGuid === NO_GRADE ? null : gradeGuid,
        salary: canSalary ? salary ?? 0 : null,
        bonus: canSalary ? bonus ?? 0 : null,
      })
      setLoading(false)

      if (ok) {
        onClose()
      }
    }

    const option = (items: SelectItem[], id: string) => items.find((i) => i.id === id) ?? null

    return (
      <Dialog
        icon="info-sign"
        title="Перевод"
        canEscapeKeyClose={true}
        canOutsideClickClose={false}
        isOpen={isOpen}
        onClose={onClose}
        style={{ width: "620px" }}
        backdropClassName="standard"
        className={`figma-dialog ${styles.dialog}`}
      >
        <div className={`${Classes.DIALOG_BODY} ${styles.body}`}>
          <LocalLoader active={loading} />

          {portfolios.length > 0 && (
            <div className={styles.field}>
              <label>Организация</label>
              <SimpleSelect
                className={`planr-default-input ${Classes.FILL}`}
                filterable={false}
                activeItem={option(portfolios, portfolioId)}
                items={portfolios}
                itemRenderer={renderSingleOption}
                onItemSelect={(i: SelectItem) => setPortfolioId(i.id)}
              >
                <DefaultSelectedOption option={option(portfolios, portfolioId)} />
              </SimpleSelect>
              {organizationChanged && (
                <>
                  <div className={styles.check} onClick={() => setResetSeniority(!resetSeniority)}>
                    <GeneralIcon type={resetSeniority ? "general-checkbox" : "general-zero-checkbox"} />
                    Сбросить стаж
                  </div>
                  <div className={styles.note}>
                    {resetSeniority
                      ? "Стаж и доплата за стаж будут считаться заново с даты перевода"
                      : `Дата перевода станет датой приёма в организацию, стаж сохранится${
                          employer.seniorityDate ? ` (с ${dateText(employer.seniorityDate)})` : ""
                        }`}
                  </div>
                </>
              )}
            </div>
          )}

          {departments.length > 0 && (
            <div className={styles.field}>
              <label>Подразделение</label>
              <SimpleSelect
                className={`planr-default-input ${Classes.FILL}`}
                filterable={true}
                itemPredicate={filterItemPredicate}
                activeItem={option(departments, departmentId)}
                inputProps={{ placeholder: "Поиск подразделения" }}
                items={departments}
                itemRenderer={renderSingleOption}
                onItemSelect={(i: SelectItem) => setDepartmentId(i.id)}
              >
                <DefaultSelectedOption option={option(departments, departmentId)} />
              </SimpleSelect>
            </div>
          )}

          <div className={styles.field}>
            <label>Должность</label>
            <SimpleSelect
              className={`planr-default-input ${Classes.FILL}`}
              filterable={true}
              itemPredicate={filterItemPredicate}
              activeItem={option(positionItems, positionId)}
              inputProps={{ placeholder: "Поиск должности" }}
              items={positionItems}
              itemRenderer={renderSingleOption}
              onItemSelect={(i: SelectItem) => selectPosition(i.id)}
            >
              <DefaultSelectedOption option={option(positionItems, positionId)} />
            </SimpleSelect>
          </div>

          {grades.length > 0 && (
            <div className={styles.field}>
              <label>Ранг</label>
              <SimpleSelect
                className={`planr-default-input ${Classes.FILL}`}
                filterable={false}
                activeItem={option(gradeItems, gradeGuid)}
                items={gradeItems}
                itemRenderer={renderSingleOption}
                onItemSelect={(i: SelectItem) => setGradeGuid(i.id)}
              >
                <DefaultSelectedOption option={option(gradeItems, gradeGuid)} />
              </SimpleSelect>
            </div>
          )}

          <div className={styles.field}>
            <label>С даты</label>
            {/* без minDate: прошедшую дату DateInput не отдаёт в onChange, а пишет в поле «Out of range»,
                и в форме остаётся прежняя дата - перевод ушёл бы с ней. Прошлое ловит dateError */}
            <DatePicker
              clasName="planr-default-input"
              value={date}
              onChange={(value) => setDate(value)}
              small
            />
            {dateError && <div className={styles.error}>{dateError}</div>}
          </div>

          {canSalary && (
            <div className={styles.row}>
              <div className={styles.field}>
                <label>Оклад</label>
                <MoneyInput
                  className="planr-default-input"
                  value={salary ?? ""}
                  onMoneyChange={(money) => setSalary(money.floatValue ?? 0)}
                />
              </div>
              <div className={styles.field}>
                <label>Премия</label>
                <MoneyInput
                  className="planr-default-input"
                  value={bonus ?? ""}
                  onMoneyChange={(money) => setBonus(money.floatValue ?? 0)}
                />
              </div>
            </div>
          )}

          {canSalary && (
            <div className={styles.note}>
              Оклад и премия действуют с месяца перевода до следующего изменения и сразу попадают в штатное расписание
            </div>
          )}
        </div>

        <div className={`${Classes.DIALOG_FOOTER} ${styles.buttons}`}>
          <PlanrButton type="greenish" size="small" onClick={save} disabled={!canSave}>
            Перевести
          </PlanrButton>
          <PlanrButton type="graybtn" size="small" onClick={onClose}>
            Отмена
          </PlanrButton>
        </div>
      </Dialog>
    )
  }
)
